import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "src/app/moov-common/base/base.component";
import { ClaroAlertTypeEnum } from "src/app/moov-common/components/claro-alert/claro-alert.component";
import { ConfirmationDialogComponent } from "src/app/moov-common/components/confirmation-dialog/confirmation-dialog.component";
import {
    StartNewQuestDialogComponent,
    StartNewQuestDialogData,
} from "src/app/moov-common/components/start-new-quest-dialog/start-new-quest-dialog.component";
import { GenderPipe } from "src/app/moov-common/pipes/gender.pipe";
import {
  StepTypeEnum,
  StructuredProcessData,
  StructuredProcessInstance,
  StructuredProcessInstanceStatusEnum,
  StructuredProcessIntroDoc,
  StructuredProcessStageData,
  StructuredProcessStageInstanceData,
  StructuredProcessStepData,
  StructuredProcessStepInstanceData,
  UserCompetenciesData,
} from "src/app/moov-common/services/chat/strcutredProcess-data";
import { AbstractStructuredProcessService } from "src/app/moov-common/services/chat/structured-process.abstract.service";
import { AbstractChatService } from "src/app/moov-common/services/chat/chat.abstract.service";
import { StructuredProcessArtifactType } from "src/app/moov-common/services/chat/structuredProcess-artifactData";
import { ClaroEvents } from "src/app/moov-common/services/google-analytics/analytics.enum";
import { AccountTypeEnum } from "src/app/moov-common/services/personal-user-data/personal-user-data";
import { AbstractDiscoveryProcessService } from "src/app/discovery-process/discovery-process.abstract.service";
import { NAVIGATION_ROUTES } from "src/app/navigation-routes.const";
import { StructuredProcessArtifactViewWorkplanComponent } from "../structured-process-artifact-view-workplan/structured-process-artifact-view-workplan.component";
import { StructuredProcessChatComponent } from "../structured-process-chat/structured-process-chat.component";
import { StructuredProcessStateService } from "src/app/moov-common/services/chat/structured-process-state.service";
import { concat, forkJoin, Observable, of, Subject } from "rxjs";
import { last, switchMap, takeUntil } from "rxjs/operators";

@Component({
    selector: "app-structured-process-view",
    templateUrl: "./structured-process-view.component.html",
    styleUrls: ["./structured-process-view.component.scss"],
})
export class StructuredProcessViewComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() processId: string; // states which process to display

    @ViewChild("processChat") processChat: StructuredProcessChatComponent;
    @ViewChild("structuredProcessArtifactViewWorkplan")
    structuredProcessArtifactViewWorkplan: StructuredProcessArtifactViewWorkplanComponent;
    @ViewChild("cvFileInput") cvFileInput!: ElementRef<HTMLInputElement>;

    bPresentArtifactView = false;
    isArtifactViewAvailable = true;
    imageRootFolder: string = "/assets/common/";
    process: StructuredProcessData;
    isProcessLoading: boolean = false;
    currentProcessId: string = "";
    currentStageId: string = "";
    currentStepId: string = "";
    currentStepStatus: StructuredProcessInstanceStatusEnum = StructuredProcessInstanceStatusEnum.PENDING;
    structuredProcessInstanceStatusEnumInstance = StructuredProcessInstanceStatusEnum;
    isProcessStarted: boolean = false;
    processInstance: StructuredProcessInstance;
    presentStartProcessIntro: boolean = false;
    presentCVAlertToUser: boolean = false;
    userJustUploadedCVData: boolean = false;
    alertTypeEnum = ClaroAlertTypeEnum;
    isGeneratingArtifact: boolean = false;
    isFirstTimeArtifactCreation: boolean = false;
    // the step the first workplan was created in - its "Ready to Make it happen?" alert belongs to that step only
    firstArtifactCreationStepId: string | undefined;
    introDoc: StructuredProcessIntroDoc;
    bPresentCompentenciesView: boolean = false;
    bPresentIntroConsentView: boolean = false;
    bPresentExternalInfoView: boolean = false;
    currStep: StructuredProcessStepData | undefined;
    isEndOfProcess: boolean = false;
    bForceMenuPresentation: boolean = false;
    userDismissedEndOfProcessAlert: boolean = false;
    userDismissedGoodbyeAlert: boolean = false;
    isB2C: boolean = false;
    cvAlertTextKey: string = "structuredProcessView.noCVAlertText";
    private viewDestroy$ = new Subject<void>();

    constructor(
        private readonly structuredProcessService: AbstractStructuredProcessService,
        private readonly discoveryProcessService: AbstractDiscoveryProcessService,
        private readonly dialog: MatDialog,
        private readonly translate: TranslateService,
        private readonly genderSerivce: GenderPipe,
        private readonly router: Router,
        private readonly processStateService: StructuredProcessStateService,
        private readonly chatService: AbstractChatService,
    ) {
        super();
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.checkForAlerts();
        this.getStructuredProcessData();

        // Listen for step selections from sidebar
        this.processStateService.stepSelected$
            .pipe(takeUntil(this.viewDestroy$))
            .subscribe((stepId) => {
                this.onStepSelected(stepId);
            });

        // Listen for restart requests from sidebar
        this.processStateService.restartStep$
            .pipe(takeUntil(this.viewDestroy$))
            .subscribe((stepId) => {
                this.onRestartStep(stepId);
            });

        // Listen for "Reset all MyQuest" from sidebar
        this.processStateService.resetProcess$
            .pipe(takeUntil(this.viewDestroy$))
            .subscribe(() => {
                this.confirmResetProcess();
            });

        // Listen for CV upload clicks from sidebar
        this.processStateService.cvUploadClicked$
            .pipe(takeUntil(this.viewDestroy$))
            .subscribe(() => {
                this.openSelectCVFileSeletionDialog();
            });

        // Listen for workplan toggle from sidebar
        this.processStateService.switchToArtifactClicked$
            .pipe(takeUntil(this.viewDestroy$))
            .subscribe(() => {
                this.presentArtifactToggle();
            });
    }

    ngOnDestroy(): void {
        this.processStateService.isProcessActive$.next(false);
        this.viewDestroy$.next();
        this.viewDestroy$.complete();
    }

    private static readonly B2C_PROCESS_ID = "SP_7b71514b296d";
    private static readonly B2U_PROCESS_ID = "SP_a4f9c832e17b";

    getStructuredProcessData(): void {
        this.isProcessLoading = true;
        const processId = this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT
            ? StructuredProcessViewComponent.B2C_PROCESS_ID
            : this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT_US
                ? StructuredProcessViewComponent.B2U_PROCESS_ID
                : "";
        this.structuredProcessService.getStructuredProcess(processId).subscribe((process) => {
            this.process = process;
            this.currentProcessId = process.id;
            this.isB2C = process.id === StructuredProcessViewComponent.B2C_PROCESS_ID || process.id === StructuredProcessViewComponent.B2U_PROCESS_ID;
            this.cvAlertTextKey = this.isB2C ? "structuredProcessView.noCVAlertText_b2c" : "structuredProcessView.noCVAlertText";
            this.introDoc = process.introDoc as StructuredProcessIntroDoc;
            this.isProcessLoading = false;

            this.processStateService.processData$.next(process);
            this.processStateService.isB2C$.next(this.isB2C);

            this.getProcessInstance();
        });
    }

    checkForAlerts(): void {
        this.personalUserDataServiceForBaseClass.getUserCVMetadata().subscribe((res) => {
            if (res) {
                if (!res.isAnalyzed) {
                    this.presentCVAlertToUser = true;
                } else {
                    this.presentCVAlertToUser = false;
                }
            }
            // no CV metadata - set alert
            else {
                this.presentCVAlertToUser = true;
            }
            // we want to reset the userJustUploadedCVData flag
            this.userJustUploadedCVData = false;
            this.updateSidebarCVUpload();
        });
    }

    private updateSidebarCVUpload(): void {
        const shouldShow = this.presentCVAlertToUser
            && !this.userJustUploadedCVData
            && (!this.isB2C || this.getCurrentStepNumber() === 4);
        this.processStateService.showCVUpload$.next(shouldShow);
    }

    startStructuredProcess(): void {
        this.structuredProcessService.startProcess().subscribe({
            next: (processInstance) => {
                if (!processInstance) {
                    this.isProcessLoading = false;
                    this.presentStartProcessIntro = true;
                    return;
                }
                this.processInstance = processInstance;
                this.currentStepId = processInstance.currentStepId;
                this.isProcessStarted = true;
                this.currentStageId = this.findStageByStep(this.currentStepId) ?? "";

                this.currentStepStatus = this.getCurrStepStatus();

                this.evaluateCurrStep(this.currentStepId);
                this.isProcessLoading = false;
                this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_ProcessStarted, {
                    processId: this.currentProcessId,
                });
            },
            error: () => {
                this.isProcessLoading = false;
                this.presentStartProcessIntro = true;
            },
        });
    }

    onStepSelected(stepId: string) {
        this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepSelected, {
            processId: this.currentProcessId,
            stepId: stepId,
        });
        // B2C users can navigate freely to any step
        const isB2C = this.isB2C;
        // if the selected step is DONE - it is ok - if not run the verification
        // leaving the "Ready to go" hand-off skips nothing worth warning about, so no verification there either
        const stepInstance = this.getStepInstanceById(stepId);
        if (!isB2C && !this.isB2BHandoffStep && stepInstance && stepInstance.status != StructuredProcessInstanceStatusEnum.COMPLETED) {
            // console.log(`StructuredProcessViewComponent onStepSelected stepId is ${stepId} and status is ${stepInstance.status}`);
            // verify that the step is the next one to our current step
            const nextStepId = this.getNextNotDoneStepId(this.currentStepId);
            // console.log(`StructuredProcessViewComponent onStepSelected nextStepId is ${nextStepId}`);
            if (nextStepId !== stepId) {
                const title = this.translate.instant("structuredProcessView.stepSelectionWarningTitle");
                const message = this.translate.instant(
                    this.genderSerivce.transform("structuredProcessView.stepSelectionWarningMessage"),
                );
                const confirmButtonText = this.translate.instant(
                    this.genderSerivce.transform("structuredProcessView.stepSelectionWarningConfirmButtonText"),
                );
                const cancelButtonText = this.translate.instant(
                    this.genderSerivce.transform("structuredProcessView.stepSelectionWarningCancelButtonText"),
                );

                // raise a warning - and let the user decide
                const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    data: {
                        title: title,
                        message: message,
                        confirmButtonText: confirmButtonText,
                        cancelButtonText: cancelButtonText,
                    },
                    height: "fit-content",
                    width: this.isMobileView ? "90%" : "30%",
                    direction: this.isRTL ? "rtl" : "ltr",
                });

                dialogRef.componentInstance.confirm.subscribe(() => {
                    this.changeStep(stepId);
                    this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepSkipped, {
                        processId: this.currentProcessId,
                        stepId: stepId,
                    });
                });

                dialogRef.componentInstance.cancel.subscribe(() => {
                    // user changed his mind - revert sidebar to previous step
                    this.processStateService.currentStepId$.next(this.currentStepId);
                    this.processStateService.currentStageId$.next(this.currentStageId);
                    this.processStateService.forceStageExpand$.next(this.currentStageId);
                });
            } else {
                // this is the next step - change it
                this.changeStep(stepId);
            }
        } else {
            // no verification was needed - change the step
            this.changeStep(stepId);
        }
    }

    changeStep(stepId: string): void {
        const stageId: string | undefined = this.findStageByStep(stepId);
        if (stageId) {
            const finalStageId = this.getFinalStageId();
            // the final stage warning copy is written for B2C/B2U ("finding your new role") - B2B goes straight in
            const isEnteringFinalStage = this.isB2C && stageId === finalStageId && this.currentStageId !== finalStageId;

            if (isEnteringFinalStage) {
                this.showFinalStageWarning(stepId, stageId);
            } else {
                this.executeStepChange(stepId, stageId);
            }
        }
    }

    private showFinalStageWarning(stepId: string, stageId: string): void {
        const title = this.translate.instant("structuredProcessView.finalStageWarningTitle");
        const message = this.translate.instant("structuredProcessView.finalStageWarningMessage");
        const confirmButtonText = this.translate.instant("structuredProcessView.finalStageWarningConfirmButtonText");
        const cancelButtonText = this.translate.instant("structuredProcessView.finalStageWarningCancelButtonText");

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: title,
                message: message,
                confirmButtonText: confirmButtonText,
                cancelButtonText: cancelButtonText,
            },
            height: "fit-content",
            width: this.isMobileView ? "90%" : "30%",
            direction: this.isRTL ? "rtl" : "ltr",
        });

        dialogRef.componentInstance.confirm.subscribe(() => {
            this.executeStepChange(stepId, stageId);
        });

        dialogRef.componentInstance.cancel.subscribe(() => {
            this.processStateService.currentStepId$.next(this.currentStepId);
            this.processStateService.currentStageId$.next(this.currentStageId);
            this.processStateService.forceStageExpand$.next(this.currentStageId);
        });
    }

    private executeStepChange(stepId: string, stageId: string): void {
        // Update step status from current instance BEFORE evaluating,
        // so child components (e.g. intro-consent) get the correct status on init
        const stepInstance = this.getStepInstanceById(stepId);
        this.currentStepStatus = stepInstance?.status ?? StructuredProcessInstanceStatusEnum.PENDING;

        // the chosen step opens directly, even from the workplan view - no "View Discussion" detour
        this.presentArtifactToggle(false);

        // Evaluate step flags BEFORE updating inputs to prevent chat component
        // from making API calls for non-chat steps (e.g. INTRO_CONSENT)
        this.evaluateCurrStep(stepId);
        this.currentStageId = stageId;
        this.currentStepId = stepId;
        this.processStateService.currentStepId$.next(stepId);
        this.processStateService.currentStageId$.next(stageId);
        this.updateSidebarCVUpload();

        this.structuredProcessService.setStructuredProcessCurrentStep(stepId).subscribe((processInstance) => {
            this.processInstance = processInstance;
            this.currentStepStatus = this.getCurrStepStatus();

            this.evaluateCurrStep(this.currentStepId);

            this.processStateService.processInstance$.next(processInstance);

            this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepViewed, {
                processId: this.currentProcessId,
                stepId: stepId,
            });
        });
    }
    onStartNextStep(): void {
        // get the next step
        // console.log(`StructuredProcessViewComponent onStartNextStep currentStepId is ${this.currentStepId}`);
        const isB2C = this.isB2C;

        // B2B: Continue in the step that created the workplan opens the workplan, not the next step
        if (!isB2C && !this.bPresentArtifactView && this.currentStepId === this.getWorkplanCreationStepId()) {
            this.isFirstTimeArtifactCreation = false;
            this.presentArtifactToggle(true);
            return;
        }

        // For EXTERNAL_INFO steps and the B2B hand-off step, the Continue tap is what completes the step:
        // mark current step as COMPLETED first, then let getProcessInstance advance currentStepId on the server side.
        // Nothing else completes them, so without this the stage and the process never complete.
        if ((this.currStep?.stepType === StepTypeEnum.EXTERNAL_INFO || this.isB2BHandoffStep)
            && this.currentStepStatus !== StructuredProcessInstanceStatusEnum.COMPLETED) {
            this.structuredProcessService
                .setStructuredProcessStepStateFromUser(
                    this.currentStepId,
                    StructuredProcessInstanceStatusEnum.COMPLETED,
                )
                .subscribe(() => {
                    const nextStepId = this.getNextSequentialStepId(this.currentStepId);
                    if (nextStepId) {
                        const nextStageId = this.findStageByStep(nextStepId);
                        if (nextStageId) {
                            this.processStateService.currentStepId$.next(nextStepId);
                            this.processStateService.currentStageId$.next(nextStageId);
                            this.processStateService.forceStageExpand$.next(nextStageId);
                            this.changeStep(nextStepId);
                            this.getProcessInstance(true);
                        }
                    }
                });
            return;
        }

        // the hand-off step always leads into the final stage, even if earlier steps were reopened since
        const nextStepId = isB2C || this.isB2BHandoffStep
            ? this.getNextSequentialStepId(this.currentStepId)
            : this.getNextNotDoneStepId(this.currentStepId);
        if (nextStepId) {
            const nextStageId = this.findStageByStep(nextStepId);

            if (nextStageId) {
                this.processStateService.currentStepId$.next(nextStepId);
                this.processStateService.currentStageId$.next(nextStageId);
                this.processStateService.forceStageExpand$.next(nextStageId);
                this.changeStep(nextStepId);

                this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_NextStepTriggered, {
                    processId: this.currentProcessId,
                    stepId: nextStepId,
                });
                // console.log(`StructuredProcessViewComponent onStartNextStep nextStepId is ${nextStepId} and nextStageId is ${nextStageId}`);
            }
        }
    }

    getCurrentStepNumber(): number {
        if (!this.process || !this.currentStepId) return 0;
        let stepNumber = 0;
        const stages = [...this.process.stages].sort((a, b) => a.idx - b.idx);
        for (const stage of stages) {
            const steps = [...stage.steps].sort((a, b) => a.idx - b.idx);
            for (const step of steps) {
                stepNumber++;
                if (step.id === this.currentStepId) return stepNumber;
            }
        }
        return 0;
    }

    getNextSequentialStepId(stepId: string): string | undefined {
        const allSteps: string[] = [];
        const stages = [...this.process.stages].sort((a, b) => a.idx - b.idx);
        for (const stage of stages) {
            const steps = [...stage.steps].sort((a, b) => a.idx - b.idx);
            for (const step of steps) {
                allSteps.push(step.id);
            }
        }
        const currentIndex = allSteps.indexOf(stepId);
        if (currentIndex >= 0 && currentIndex < allSteps.length - 1) {
            return allSteps[currentIndex + 1];
        }
        return undefined;
    }

    getNextNotDoneStepId(stepId: string): string | undefined {
        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId stepId is ${stepId}`);
        const stageId = this.findStageByStep(stepId);

        if (!stageId) {
            return undefined;
        }

        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId stageId is ${stageId}`);
        const stage = this.getStageInstanceById(stageId);

        if (!stage) {
            // console.log(`StructuredProcessViewComponent getNextNotDoneStepId stage was undefined`);
            return undefined;
        }

        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId stage is ${stage.stageId}`);

        // find the next unfinished step in the current stage
        const currStageSteps = stage.stepsInstances.sort((a, b) => a.idx - b.idx);
        let firstUnfinishedStep = currStageSteps.find(
            (s: any) => s.status !== StructuredProcessInstanceStatusEnum.COMPLETED,
        );

        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId firstUnfinishedStep is ${JSON.stringify(firstUnfinishedStep)}`);

        if (firstUnfinishedStep) {
            return firstUnfinishedStep.stepId;
        }

        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId firstUnfinishedStep was undefined`);

        // find the next unfinished stage
        const stages = this.processInstance.stagesInstances.sort((a, b) => a.idx - b.idx);
        const firstUnfinishedStage = stages.find(
            (s: any) => s.status !== StructuredProcessInstanceStatusEnum.COMPLETED,
        );

        // console.log(`StructuredProcessViewComponent getNextNotDoneStepId firstUnfinishedStage is ${JSON.stringify(firstUnfinishedStage)}`);

        if (firstUnfinishedStage) {
            const nextStageSteps = firstUnfinishedStage.stepsInstances.sort((a, b) => a.idx - b.idx);
            firstUnfinishedStep = nextStageSteps.find(
                (s: any) => s.status !== StructuredProcessInstanceStatusEnum.COMPLETED,
            );

            if (firstUnfinishedStep) {
                return firstUnfinishedStep.stepId;
            }
        }

        return undefined;
    }

    getNextStage(stageId: string): StructuredProcessStageData | undefined {
        const stages = this.process.stages.sort((a, b) => a.idx - b.idx);
        const nextStage = stages.find((stage) => stage.idx > stage.idx);
        return nextStage;
    }

    getFinalStageId(): string | undefined {
        if (!this.process?.stages?.length) {
            return undefined;
        }
        const sorted = [...this.process.stages].sort((a, b) => a.idx - b.idx);
        return sorted[sorted.length - 1].id;
    }

    findStageByStep(stepId: string): string | undefined {
        const stage = this.process.stages.find((stage) => stage.steps.some((step) => step.id === stepId));

        if (stage) {
            return stage.id;
        }

        return undefined;
    }

    getStepById(stepId: string): StructuredProcessStepData | undefined {
        for (const stage of this.process.stages) {
            const step = stage.steps.find((step) => step.id === stepId);
            if (step) {
                return step;
            }
        }
        return undefined;
    }

    getStageInstanceById(stageId: string): StructuredProcessStageInstanceData | undefined {
        return this.processInstance.stagesInstances.find((stage) => stage.stageId === stageId);
    }

    getStepInstanceById(stepId: string): StructuredProcessStepInstanceData | undefined {
        for (const stage of this.processInstance.stagesInstances) {
            const step = stage.stepsInstances.find((step) => step.stepId === stepId);
            if (step) {
                return step;
            }
        }
        return undefined;
    }

    onNewMessageReceived(): void {
        this.getProcessInstance(true);
    }

    getProcessInstance(silent: boolean = false): void {
        // console.log(`getProcessInstance, silent: ${silent}`);
        if (!silent) {
            this.isProcessLoading = true;
        }

        this.structuredProcessService.getStructuredProcessInstance().subscribe({
            next: (processInstance) => {
            this.isProcessLoading = false;

            if (processInstance) {
                // process instance found, show the process
                this.presentStartProcessIntro = false;
                this.processInstance = processInstance;
                this.currentStepId = processInstance.currentStepId;
                this.isProcessStarted = true;
                this.currentStageId = this.findStageByStep(this.currentStepId) ?? "";

                this.currentStepStatus = this.getCurrStepStatus();

                this.evaluateCurrStep(this.currentStepId);

                // Push state to shared service for sidebar
                this.processStateService.processInstance$.next(processInstance);
                this.processStateService.currentStepId$.next(this.currentStepId);
                this.processStateService.currentStageId$.next(this.currentStageId);
                this.processStateService.isProcessActive$.next(true);

                if (this.processStateService.pendingStepId) {
                    const pendingId = this.processStateService.pendingStepId;
                    this.processStateService.pendingStepId = null;
                    this.onStepSelected(pendingId);
                }

                if (this.currentStepStatus === StructuredProcessInstanceStatusEnum.COMPLETED) {
                    this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepStatusIsCompleted, {
                        processId: this.currentProcessId,
                        stepId: this.currentStepId,
                    });
                }

                // check if the artifact is available and if it is being generated
                const wasGeneratingArtifact = this.isGeneratingArtifact;
                if (
                    this.processInstance.artifactCreationRequestTimestamp &&
                    this.processInstance.artifactCreationRequestTimestamp[StructuredProcessArtifactType.WORKPLAN]
                ) {
                    this.isArtifactViewAvailable = true;

                    if (
                        this.processInstance.artifactCreationTimestamp &&
                        this.processInstance.artifactCreationTimestamp[StructuredProcessArtifactType.WORKPLAN]
                    ) {
                        if (
                            this.processInstance.artifactCreationTimestamp[StructuredProcessArtifactType.WORKPLAN] >
                            this.processInstance.artifactCreationRequestTimestamp[
                                StructuredProcessArtifactType.WORKPLAN
                            ]
                        ) {
                            // last artifact was created after the request
                            this.isGeneratingArtifact = false;
                        } else {
                            // last artifact was created before the request
                            this.isGeneratingArtifact = true;
                        }
                    } else {
                        // artifact was never requested - this is the first time - present artifact view
                        this.isGeneratingArtifact = true;
                        this.isArtifactViewAvailable = false;
                        this.isFirstTimeArtifactCreation = true;
                        this.firstArtifactCreationStepId = this.currentStepId;
                        this.presentArtifactToggle(true);
                    }
                } else {
                    // artifact was not requested
                    this.isGeneratingArtifact = false;
                    this.isArtifactViewAvailable = false;
                    this.isFirstTimeArtifactCreation = false;
                }

                // a generation just started - show its loader in the main area (the workplan view);
                // only on the switch, so a user who goes back to the chat while it runs stays there
                if (this.isGeneratingArtifact && !wasGeneratingArtifact && !this.bPresentArtifactView) {
                    this.presentArtifactToggle(true);
                }

                this.processStateService.isArtifactViewAvailable$.next(this.isArtifactViewAvailable);
                this.processStateService.isGeneratingArtifact$.next(this.isGeneratingArtifact);
                this.processStateService.isPresentingArtifactView$.next(this.bPresentArtifactView);

                if (this.isGeneratingArtifact) {
                    this.scheduleArtifactCheck();
                } else if (this.structuredProcessArtifactViewWorkplan && this.isArtifactViewAvailable) {
                    // not generating artifact, but artifact view is available, update the workplan
                    this.structuredProcessArtifactViewWorkplan.updateWorkplan();
                }
            } else {
                // no process instance found - start it right away (the intro is the first step of the process).
                // Not on a silent refresh for B2B: start replaces the instance, so a refresh must never reset a running process.
                if (!silent || this.isB2C) {
                    if (!silent) {
                        // keep the loader until the start returns so the fallback intro screen doesn't flash
                        this.isProcessLoading = true;
                    }
                    this.startStructuredProcess();
                }
            }
            },
            error: () => {
                this.isProcessLoading = false;
                // one failed refresh must not end the wait for a workplan that is still being generated
                if (silent && this.isGeneratingArtifact) {
                    this.scheduleArtifactCheck();
                    return;
                }
                if (this.isB2C) {
                    this.startStructuredProcess();
                } else {
                    this.presentStartProcessIntro = true;
                }
            },
        });
    }

    private scheduleArtifactCheck(): void {
        // artifact is still being generated, check again in 5 seconds
        setTimeout(() => {
            this.getProcessInstance(true);
        }, 5000);
    }

    getCurrStepStatus(): StructuredProcessInstanceStatusEnum {
        if (this.processInstance && this.currentStepId && this.currentStepId != "") {
            const stepInstance = this.getStepInstanceById(this.currentStepId);
            if (stepInstance) {
                // console.log(`StructuredProcessViewComponent getCurrStepStatus stepId ${this.currentStepId} status is ${stepInstance.status}`);
                return stepInstance.status;
            }
        }
        return StructuredProcessInstanceStatusEnum.PENDING;
    }

    onRestartStep(stepId: string): void {
        // console.log(`StructuredProcessViewComponent onRestartStep stepId is ${stepId}`);
        const workplanStepIds = this.getStepIdsRestartedWithWorkplan(stepId);
        if (workplanStepIds.length > 0) {
            this.confirmRestartStepsWithWorkplan(workplanStepIds);
            return;
        }

        const title = this.translate.instant("structuredProcessView.restartStepTitle");
        const message = this.translate.instant(
            this.genderSerivce.transform("structuredProcessView.restartStepMessage"),
        );
        const confirmButtonText = this.translate.instant("structuredProcessView.restartStepButtonConfirmText");
        const cancelButtonText = this.translate.instant("structuredProcessView.restartStepCancelButtonText");

        // raise a warning - and let the user decide
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: title,
                message: message,
                confirmButtonText: confirmButtonText,
                cancelButtonText: cancelButtonText,
            },
            height: "fit-content",
            width: this.isMobileView ? "90%" : "30%",
            direction: this.isRTL ? "rtl" : "ltr",
        });

        dialogRef.componentInstance.confirm.subscribe(() => {
            this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepRestarted, {
                processId: this.currentProcessId,
                stepId: stepId,
            });

            if (stepId !== this.currentStepId) {
                // resetting a step that isn't open (e.g. "Ready to go" from Make It Happen) -
                // the open chat must stay, only the reset step's own chat is cleared
                this.restartStepInBackground(stepId);
                return;
            }

            // Set status to PENDING immediately so child components reset before API call
            this.currentStepStatus = StructuredProcessInstanceStatusEnum.PENDING;
            this.evaluateCurrStep(stepId);
            if (this.currStep && this.currStep.stepType != StepTypeEnum.COMPETENCIES && this.currStep.stepType != StepTypeEnum.INTRO_CONSENT && this.currStep.stepType != StepTypeEnum.EXTERNAL_INFO) {
                this.processChat.restartStep(stepId);
            }
            if (this.currStep && this.currStep.stepType == StepTypeEnum.INTRO_CONSENT) {
                // Reset discovery journey so it starts fresh
                this.discoveryProcessService.startUserJourney().subscribe();
            }

            this.structuredProcessService
                .setStructuredProcessStepStateFromUser(stepId, StructuredProcessInstanceStatusEnum.PENDING)
                .subscribe((processInstance) => {
                    this.processInstance = processInstance;
                    // console.log(`StructuredProcessViewComponent onRestartStep calling getCurrStepStatus`);
                    this.currentStepStatus = this.getCurrStepStatus();
                });
        });
    }

    // The first workplan step creates the workplan ("Creating Action Plan") and the steps before it in its stage feed it
    // ("Setting Goals"), so restarting one of them restarts every step up to the creating step and resets the workplan too.
    // Later workplan steps (the weekly update in "Make It Happen") only update the plan and keep it.
    private getStepIdsRestartedWithWorkplan(stepId: string): string[] {
        const isWorkplanStep = (step: StructuredProcessStepData) => step.artifactType === StructuredProcessArtifactType.WORKPLAN;
        const stage = [...this.process.stages].sort((a, b) => a.idx - b.idx).find((stage) => stage.steps.some(isWorkplanStep));
        if (!stage) {
            return [];
        }
        const steps = [...stage.steps].sort((a, b) => a.idx - b.idx);
        const from = steps.findIndex((step) => step.id === stepId);
        const creatingStep = steps.findIndex(isWorkplanStep);
        return from === -1 || from > creatingStep ? [] : steps.slice(from, creatingStep + 1).map((step) => step.id);
    }

    // the first workplan step of the process creates the workplan ("Creating Action Plan")
    private getWorkplanCreationStepId(): string | undefined {
        for (const stage of [...this.process.stages].sort((a, b) => a.idx - b.idx)) {
            const step = [...stage.steps].sort((a, b) => a.idx - b.idx).find((step) => step.artifactType === StructuredProcessArtifactType.WORKPLAN);
            if (step) {
                return step.id;
            }
        }
        return undefined;
    }

    private confirmRestartStepsWithWorkplan(stepIds: string[]): void {
        const steps = stepIds.map((id) => `"${this.getStepById(id)?.header}"`).join(", ");
        const textKey = stepIds.length === 1 ? "restartStepWithWorkplan" : "restartStepsWithWorkplan";
        const title = this.translate.instant(`structuredProcessView.${textKey}Title`);
        const message = this.translate.instant(
            this.genderSerivce.transform(`structuredProcessView.${textKey}Message`),
            { count: stepIds.length, steps: steps },
        );
        const confirmButtonText = this.translate.instant("structuredProcessView.restartStepsWithWorkplanButtonConfirmText");
        const cancelButtonText = this.translate.instant("structuredProcessView.restartStepCancelButtonText");

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: title,
                message: message,
                confirmButtonText: confirmButtonText,
                cancelButtonText: cancelButtonText,
            },
            height: "fit-content",
            width: this.isMobileView ? "90%" : "30%",
            direction: this.isRTL ? "rtl" : "ltr",
        });

        dialogRef.componentInstance.confirm.subscribe(() => {
            for (const stepId of stepIds) {
                this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_StepRestarted, {
                    processId: this.currentProcessId,
                    stepId: stepId,
                });
            }
            this.restartStepsWithWorkplan(stepIds);
        });
    }

    private restartStepsWithWorkplan(stepIds: string[]): void {
        const firstStepId = stepIds[0];
        // the open chat of the first step restarts in place; every other chat is deleted before the user lands on it
        const restartInPlace = this.currentStepId === firstStepId && !this.bPresentArtifactView && !!this.processChat;
        if (restartInPlace) {
            this.currentStepStatus = StructuredProcessInstanceStatusEnum.PENDING;
            this.processChat.restartStep(firstStepId);
        }

        const chatDeletions = stepIds
            .filter((stepId) => !(restartInPlace && stepId === firstStepId))
            .map((stepId) => this.deleteStepChat(stepId));
        const stateResets = stepIds.map((stepId) =>
            this.structuredProcessService.setStructuredProcessStepStateFromUser(stepId, StructuredProcessInstanceStatusEnum.PENDING),
        );

        forkJoin(chatDeletions.length > 0 ? chatDeletions : [of(null)])
            .pipe(
                switchMap(() =>
                    concat(...stateResets, this.structuredProcessService.resetStructuredProcessArtifact(StructuredProcessArtifactType.WORKPLAN)),
                ),
                last(),
            )
            .subscribe((processInstance) => {
                this.processInstance = processInstance;
                this.processStateService.processInstance$.next(processInstance);

                // the workplan is gone - nothing to show or wait for
                this.isArtifactViewAvailable = false;
                this.isGeneratingArtifact = false;
                this.isFirstTimeArtifactCreation = false;
                this.processStateService.isArtifactViewAvailable$.next(false);
                this.processStateService.isGeneratingArtifact$.next(false);

                // a user inside the restarted steps starts again from the first of them
                if (stepIds.includes(this.currentStepId) && this.currentStepId !== firstStepId) {
                    this.executeStepChange(firstStepId, this.findStageByStep(firstStepId) ?? this.currentStageId);
                } else {
                    this.currentStepStatus = this.getCurrStepStatus();
                }
                this.presentArtifactToggle(false);
            });
    }

    private deleteStepChat(stepId: string): Observable<unknown> {
        const stageId = this.findStageByStep(stepId);
        if (this.getStepById(stepId)?.stepType !== StepTypeEnum.CHAT || !stageId) {
            return of(null);
        }
        return this.chatService
            .getStructuredProcessChat(this.currentProcessId, stageId, stepId)
            .pipe(switchMap((chat) => (chat ? this.chatService.deleteChat(chat.id) : of(null))));
    }

    private restartStepInBackground(stepId: string): void {
        const step = this.getStepById(stepId);
        this.deleteStepChat(stepId).subscribe();
        if (step?.stepType === StepTypeEnum.INTRO_CONSENT) {
            // Reset discovery journey so it starts fresh
            this.discoveryProcessService.startUserJourney().subscribe();
        }

        this.structuredProcessService
            .setStructuredProcessStepStateFromUser(stepId, StructuredProcessInstanceStatusEnum.PENDING)
            .subscribe((processInstance) => {
                this.processInstance = processInstance;
                this.processStateService.processInstance$.next(processInstance);
            });
    }

    private confirmResetProcess(): void {
        const [keptStage, ...resetStages] = this.process ? this.getStagesInOrder() : [];
        if (resetStages.length === 0) {
            return;
        }
        const firstResetStage = resetStages[0];
        const lastResetStage = resetStages[resetStages.length - 1];

        const data: StartNewQuestDialogData = {
            title: this.translate.instant(this.genderSerivce.transform("structuredProcessView.resetProcessTitle")),
            message: this.translate.instant(this.genderSerivce.transform("structuredProcessView.resetProcessMessage"), {
                stage: firstResetStage.name,
            }),
            keptText: this.translate.instant("structuredProcessView.resetProcessKeptItem", { stage: keptStage.name }),
            resetText: this.translate.instant("structuredProcessView.resetProcessResetItem", {
                firstStage: firstResetStage.name,
                lastStage: lastResetStage.name,
            }),
            confirmButtonText: this.translate.instant("structuredProcessView.resetProcessButtonConfirmText"),
            cancelButtonText: this.translate.instant("structuredProcessView.restartStepCancelButtonText"),
        };
        const dialogRef = this.dialog.open<StartNewQuestDialogComponent, StartNewQuestDialogData, boolean>(StartNewQuestDialogComponent, {
            data: data,
            width: this.isMobileView ? "90%" : "420px",
            direction: this.isRTL ? "rtl" : "ltr",
            panelClass: "start-new-quest-dialog-panel",
            ariaLabelledBy: "start-new-quest-dialog-title",
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) {
                return;
            }
            this.analytics.sendClaroEvent(ClaroEvents.StructuredProcess_ProcessReset, {
                processId: this.currentProcessId,
            });
            this.resetProcess();
        });
    }

    // The first stage ("Discover your inventory": consent, questionnaire, competencies, CV) is kept; every later stage
    // starts over - its chats and the workplan are deleted and its steps go back to pending. Each call is safe to repeat,
    // so a reset that fails half way is finished by trying again.
    private resetProcess(): void {
        if (this.isGeneratingArtifact) {
            // a workplan still being generated could land on the fresh process
            return;
        }

        const stepIds = this.getStagesInOrder()
            .slice(1)
            .flatMap((stage) => [...stage.steps].sort((a, b) => a.idx - b.idx))
            .map((step) => step.id);
        if (stepIds.length === 0) {
            return;
        }
        const chatDeletions = stepIds.map((stepId) => this.deleteStepChat(stepId));
        const stateResets = stepIds.map((stepId) =>
            this.structuredProcessService.setStructuredProcessStepStateFromUser(stepId, StructuredProcessInstanceStatusEnum.PENDING),
        );
        this.isProcessLoading = true;

        forkJoin(chatDeletions)
            .pipe(
                switchMap(() => this.structuredProcessService.resetStructuredProcessArtifact(StructuredProcessArtifactType.WORKPLAN)),
                // last on purpose: the steps start over only once everything they feed on is gone
                switchMap(() => concat(...stateResets, this.structuredProcessService.setStructuredProcessCurrentStep(stepIds[0]))),
                last(),
            )
            .subscribe({
                next: () => {
                    // nothing of the old plan stays on the page
                    this.presentArtifactToggle(false);
                    this.isFirstTimeArtifactCreation = false;
                    this.firstArtifactCreationStepId = undefined;
                    this.userDismissedEndOfProcessAlert = false;
                    this.userDismissedGoodbyeAlert = false;
                    this.getProcessInstance();
                },
                error: () => {
                    this.getProcessInstance();
                    this.confirmResetProcessRetry();
                },
            });
    }

    // the API doesn't promise the stage order, and "Start a New Quest" must keep exactly the first stage
    private getStagesInOrder(): StructuredProcessStageData[] {
        return [...this.process.stages].sort((a, b) => a.idx - b.idx);
    }

    private confirmResetProcessRetry(): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: this.translate.instant("structuredProcessView.resetProcessFailedTitle"),
                message: this.translate.instant(this.genderSerivce.transform("structuredProcessView.resetProcessFailedMessage")),
                confirmButtonText: this.translate.instant("structuredProcessView.resetProcessRetryButtonText"),
                cancelButtonText: this.translate.instant("structuredProcessView.restartStepCancelButtonText"),
            },
            height: "fit-content",
            width: this.isMobileView ? "90%" : "30%",
            direction: this.isRTL ? "rtl" : "ltr",
        });

        dialogRef.componentInstance.confirm.subscribe(() => {
            this.resetProcess();
        });
    }

    uploadCVClicked(): void {
        this.router.navigate([NAVIGATION_ROUTES.app.personalView]);
    }

    openSelectCVFileSeletionDialog(): void {
        this.cvFileInput.nativeElement.click();
    }

    uploadCV(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file: File = input.files[0];

            // Validate that the file is a PDF
            if (file.type !== "application/pdf") {
                console.error("Selected file is not a PDF.");
                return;
            }

            this.userJustUploadedCVData = false;
            this.personalUserDataServiceForBaseClass.uploadCV(file).subscribe((res) => {
                // console.log('uploadCV successfull');
                this.userJustUploadedCVData = true;
                this.updateSidebarCVUpload();
                this.analytics.sendClaroEvent(ClaroEvents.UserUploadedCV);
                console.log("uploadCV successfull, waiting 20 seconds to get CV metadata");
                setTimeout(() => {
                    this.checkForAlerts();
                }, 30000);
            });
        }
    }

    presentArtifactToggle(forceValue: boolean | null = null): void {
        if (forceValue !== null) {
            this.bPresentArtifactView = forceValue;
        } else {
            this.bPresentArtifactView = !this.bPresentArtifactView;
        }
        this.processStateService.isPresentingArtifactView$.next(this.bPresentArtifactView);
    }

    onWorkplanBackToPlan(): void {
        this.isFirstTimeArtifactCreation = false;
        this.presentArtifactToggle(true);
    }

    // shown on the step page (never over the workplan), once the first plan is ready, in the step that created it.
    // Not for B2B: MyQuest moves on with Continue, which the step already shows once the plan is ready
    get showArtifactCreationApprovedAlert(): boolean {
        return (
            this.isB2C &&
            this.isFirstTimeArtifactCreation &&
            !this.isGeneratingArtifact &&
            !this.bPresentArtifactView &&
            this.currentStepId === this.firstArtifactCreationStepId
        );
    }

    // B2B chat step right before the final stage (e.g. "Ready to go") - an informational hand-off that is
    // only done once the user taps Continue, so the CTA can't wait for the step to be marked COMPLETED
    get isB2BHandoffStep(): boolean {
        if (this.isB2C || !this.process || !this.currentStepId) return false;
        if (this.currStep?.stepType !== StepTypeEnum.CHAT) return false;
        const finalStageId = this.getFinalStageId();
        if (this.currentStageId === finalStageId) return false;
        const nextStepId = this.getNextSequentialStepId(this.currentStepId);
        return !!nextStepId && this.findStageByStep(nextStepId) === finalStageId;
    }

    get showB2CGoodbyeMessage(): boolean {
        if (!this.isB2C || !this.process || !this.currentStepId) return false;
        if (this.currentStepStatus !== StructuredProcessInstanceStatusEnum.COMPLETED) return false;
        const stage = this.process.stages.find(s => s.idx === 3);
        if (!stage) return false;
        return stage.steps.some(s => s.id === this.currentStepId && s.idx === 4);
    }

    onArtifactCreationApproved(): void {
        // artifact was created at least once before
        this.isFirstTimeArtifactCreation = false;
        this.presentArtifactToggle(false);

        // we need to wait for the artifact view to be present
        setTimeout(() => {
            this.processChat.forceUserTextToChat(
                this.translate.instant("structuredProcessView.workplan.artifactCreationApprovedMessage"),
            );
        }, 1000);
    }

    // B2C/B2U Continue always goes to the next step in order, so there is nowhere to go from the last one.
    // B2B Continue can still lead back to an unfinished earlier step.
    get hasNextStep(): boolean {
        if (!this.isB2C || !this.process || !this.currentStepId) return true;
        return !!this.getNextSequentialStepId(this.currentStepId);
    }

    get isCurrentStepCompleted(): boolean {
        return this.currentStepStatus === StructuredProcessInstanceStatusEnum.COMPLETED;
    }

    // "Reset & Start Over" in the competencies app: the step is open again until the user finishes it.
    // The saved results stay in the database until they are replaced.
    onCompetenciesReset(): void {
        this.structuredProcessService
            .setStructuredProcessStepStateFromUser(
                this.currentStepId,
                StructuredProcessInstanceStatusEnum.IN_PROGRESS,
            )
            .subscribe(() => {
                this.getProcessInstance(true);
            });
    }

    onCompetenciesCompleted(userCompetenciesData: UserCompetenciesData): void {
        console.log("onCompetenciesCompleted", JSON.stringify(userCompetenciesData));
        this.personalUserDataServiceForBaseClass.setUserCompetencies(userCompetenciesData).subscribe((res) => {
            this.structuredProcessService
                .setStructuredProcessStepStateFromUser(
                    this.currentStepId,
                    StructuredProcessInstanceStatusEnum.COMPLETED,
                )
                .subscribe(() => {
                    this.getProcessInstance(true);
                });
        });
    }

    onIntroConsentApproved(): void {
        // Persist IN_PROGRESS before the consent view moves on to the questionnaire, and keep the returned
        // instance: a stale cached PENDING status re-opened the consent screen on step navigation.
        // The questionnaire creates or resumes the journey itself (continueUserJourney) - calling
        // startUserJourney here wiped the answers of a journey that was already in progress.
        this.personalUserDataServiceForBaseClass.approvePrivacyPolicy()
            .pipe(
                switchMap(() => this.structuredProcessService.setStructuredProcessStepStateFromUser(
                    this.currentStepId, StructuredProcessInstanceStatusEnum.IN_PROGRESS)),
            )
            .subscribe((processInstance) => {
                if (!processInstance) {
                    return;
                }
                this.processInstance = processInstance;
                this.processStateService.processInstance$.next(processInstance);
                this.currentStepStatus = this.getCurrStepStatus();
            });
    }

    onDiscoveryCompleted(): void {
        this.structuredProcessService
            .setStructuredProcessStepStateFromUser(
                this.currentStepId,
                StructuredProcessInstanceStatusEnum.COMPLETED,
            )
            .subscribe(() => {
                this.getProcessInstance(true);
            });
    }

    evaluateCurrStep(id: string): void {
        const step = this.getStepById(id);

        // console.log('evaluateCurrStep step', JSON.stringify(step));

        if (step) {
            this.currStep = step;
            if (step.stepType == StepTypeEnum.COMPETENCIES) {
                this.bPresentCompentenciesView = true;
                this.bPresentIntroConsentView = false;
                this.bPresentExternalInfoView = false;
            } else if (step.stepType == StepTypeEnum.INTRO_CONSENT) {
                this.bPresentCompentenciesView = false;
                this.bPresentIntroConsentView = true;
                this.bPresentExternalInfoView = false;
            } else if (step.stepType == StepTypeEnum.EXTERNAL_INFO) {
                this.bPresentCompentenciesView = false;
                this.bPresentIntroConsentView = false;
                this.bPresentExternalInfoView = true;
            } else {
                this.bPresentCompentenciesView = false;
                this.bPresentIntroConsentView = false;
                this.bPresentExternalInfoView = false;
            }

            if (step.isEndOfProcess) {
                this.isEndOfProcess = true;
            } else {
                this.isEndOfProcess = false;
            }
        }
    }

}
